import service from './index'
import { Loading } from 'element-ui'

/**
 * 请求配置选项
 * @typedef {Object} RequestOptions
 * @property {boolean} showLoading - 是否显示loading
 * @property {boolean} showSuccessMessage - 是否显示成功消息
 * @property {boolean} showErrorMessage - 是否显示错误消息
 * @property {string} loadingText - loading文本
 * @property {number} timeout - 超时时间
 */

/**
 * 默认请求配置
 */
const defaultOptions = {
  showLoading: false,
  showSuccessMessage: true,
  showErrorMessage: true,
  loadingText: '加载中...',
  timeout: 15000
}

/**
 * Loading实例管理
 */
let loadingInstance = null
let loadingCount = 0

/**
 * 显示Loading
 * @param {string} text - loading文本
 */
function showLoading(text = '加载中...') {
  if (loadingCount === 0) {
    loadingInstance = Loading.service({
      lock: true,
      text: text,
      spinner: 'el-icon-loading',
      background: 'rgba(0, 0, 0, 0.7)'
    })
  }
  loadingCount++
}

/**
 * 隐藏Loading
 */
function hideLoading() {
  loadingCount--
  if (loadingCount <= 0) {
    loadingCount = 0
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }
  }
}

/**
 * 通用请求方法
 * @param {Object} config - axios配置
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
function request(config, options = {}) {
  // 合并配置
  const finalOptions = { ...defaultOptions, ...options }
  const finalConfig = { 
    ...config, 
    timeout: finalOptions.timeout,
    // 将选项传递给 axios config，以便拦截器可以使用
    showErrorMessage: finalOptions.showErrorMessage,
    showSuccessMessage: finalOptions.showSuccessMessage
  }
  
  // 显示loading
  if (finalOptions.showLoading) {
    showLoading(finalOptions.loadingText)
  }
  
  return service(finalConfig)
    .then(response => {
      // 隐藏loading
      if (finalOptions.showLoading) {
        hideLoading()
      }
      
      return response
    })
    .catch(error => {
      // 隐藏loading
      if (finalOptions.showLoading) {
        hideLoading()
      }
      
      // 如果配置了不显示错误消息，则重新抛出错误但不显示消息
      if (!finalOptions.showErrorMessage) {
        // 这里可以添加静默错误处理逻辑
      }
      
      return Promise.reject(error)
    })
}

/**
 * GET请求
 * @param {string} url - 请求URL
 * @param {Object} params - 请求参数
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
export function get(url, params = {}, options = {}) {
  return request({
    method: 'GET',
    url,
    params
  }, options)
}

/**
 * POST请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
export function post(url, data = {}, options = {}) {
  return request({
    method: 'POST',
    url,
    data
  }, options)
}

/**
 * PUT请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
export function put(url, data = {}, options = {}) {
  return request({
    method: 'PUT',
    url,
    data
  }, options)
}

/**
 * DELETE请求
 * @param {string} url - 请求URL
 * @param {Object} params - 请求参数
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
export function del(url, params = {}, options = {}) {
  return request({
    method: 'DELETE',
    url,
    params
  }, options)
}

/**
 * PATCH请求
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {RequestOptions} options - 请求选项
 * @returns {Promise}
 */
export function patch(url, data = {}, options = {}) {
  return request({
    method: 'PATCH',
    url,
    data
  }, options)
}

// 导出默认的request方法
// 导出request函数
export { request }

export default {
  get,
  post,
  put,
  delete: del,
  patch,
  request
}